import React from 'react';
import { SettingId } from '../../settings/registry';

interface ISettingProps {
    /** Registry identifier. Unknown ids fail to compile. */
    id: SettingId;
    /**
     * Render as a `display: contents` marker rather than a real box.
     *
     * Only for places where an extra box breaks layout that a plain block div cannot stand in for -
     * a grid cell, a flex child that must grow, or a parent with `> *` styling. Note that Fluent's
     * `Stack` applies `childrenGap` as margins on its direct children, and margins do not render on
     * a `display: contents` box, so this collapses the gap when used as a direct Stack child.
     */
    contents?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

/**
 * Marks a control as a documented setting so a `/go/setting/<id>` link can find and highlight it.
 *
 * Wrap the innermost element that represents the setting. Inside a `StackItem` or a grid cell, put
 * this within that element rather than around it, so the layout role stays on the original node.
 *
 * Shared field wrappers (DataField, ToggleField, DropdownField, ...) take a `settingId` prop
 * instead and stamp their own root - prefer that where it is available, since it adds no element.
 */
const Setting: React.FC<ISettingProps> = ({ id, contents, className, style, children }) => (
    <div
        data-setting-id={id}
        className={className}
        style={contents ? { display: 'contents', ...style } : style}
    >
        {children}
    </div>
);

/**
 * Marks a control whose identifier comes from data rather than from the registry.
 *
 * Extension schema fields, localization overrides and section style properties are named by the
 * data itself, so their ids cannot be a checked union - they are matched at resolve time by a
 * pattern in `DYNAMIC_PATTERNS` (src/settings/registry/index.ts). Use `Setting` wherever the id is
 * known ahead of time; the typed union is the guardrail and this deliberately gives it up.
 */
export const DynamicSetting: React.FC<{ id: string; className?: string; style?: React.CSSProperties }> = ({
    id,
    className,
    style,
    children,
}) => (
    <div data-setting-id={id} className={className} style={style}>
        {children}
    </div>
);

export default Setting;
